import { ambientToDepth, depthToAmbient } from "../physics/pressure.js";
import type { TissueSnapshot } from "../types.js";
import { N_COMPARTMENTS, ZHL16C } from "./constants.js";

export function ceilingAmbientForCompartment(
  pInert: number,
  a: number,
  b: number,
  gf: number,
): number {
  const denom = gf / b - gf + 1;
  return (pInert - gf * a) / denom;
}

export function overallCeilingAmbient(state: TissueSnapshot, gf: number): number {
  let max = 0;
  for (let i = 0; i < N_COMPARTMENTS; i++) {
    const cpt = ZHL16C[i]!;
    const c = ceilingAmbientForCompartment(state.pN2[i]!, cpt.aN2, cpt.bN2, gf);
    if (c > max) max = c;
  }
  return max;
}

export function overallCeilingDepth(state: TissueSnapshot, gf: number): number {
  return Math.max(0, ambientToDepth(overallCeilingAmbient(state, gf)));
}

export function leadingCompartment(state: TissueSnapshot, gf: number): number {
  let bestIdx = 0;
  let bestC = -Infinity;
  for (let i = 0; i < N_COMPARTMENTS; i++) {
    const cpt = ZHL16C[i]!;
    const c = ceilingAmbientForCompartment(state.pN2[i]!, cpt.aN2, cpt.bN2, gf);
    if (c > bestC) {
      bestC = c;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function gfAtDepth(
  depth: number,
  firstStopDepth: number,
  gfLow: number,
  gfHigh: number,
): number {
  if (firstStopDepth <= 0) return gfHigh;
  if (depth >= firstStopDepth) return gfLow;
  if (depth <= 0) return gfHigh;
  const frac = (firstStopDepth - depth) / firstStopDepth;
  return gfLow + (gfHigh - gfLow) * frac;
}

export function mValueAtDepth(depth: number, a: number, b: number): number {
  return a + depthToAmbient(depth) / b;
}

export function gfMValueAtDepth(depth: number, a: number, b: number, gf: number): number {
  const pAmb = depthToAmbient(depth);
  const m = mValueAtDepth(depth, a, b);
  return pAmb + gf * (m - pAmb);
}
